import { useEntities } from 'utils/hooks/api';
import {
    DataSourceBreadCrumbs,
    DataSourceIndustriesOnlyBreadCrumbs,
} from 'components';
import {
    FLAVORS_METADATA_METRIC,
    FLAVORS_SORT_OPTIONS,
} from 'components/FlavorFilters/utils';
import { DATA_SOURCES, TIKTOK_COUNTRIES, isCovered } from '@/config/constants';
import {
    GOOGLE_METADATA_BY_KEY,
    GOOGLE_SORT_OPTIONS,
    TIKTOK_METADATA_BY_KEY,
    TIKTOK_SORT_OPTIONS,
} from 'utils/metrics';
import {
    TikTokDrawerContent,
    TikTokDrawerContentIndividualHashtag,
} from './TikTokDrawerContent';
import {
    TikTokNameBodyCol,
    TikTokNameHashtagListCol,
} from 'components/TikTokTable';
import { useEffect } from 'react';
import {
    generatePath,
    useLocation,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import {
    getTikTokDownloadOptions,
    getTikTokDownloadOptionsAsync,
} from './utils';
import {
    useAbortController,
    useCurrentCountry,
    useTikTokData,
    useTikTokWeeks,
} from 'utils/hooks';
import DataSourceTabsByCoverage from 'components/DataSourceTabsByCoverage';
import Drawer from 'components/Drawer';
import ExploreDataSourceTable from 'components/DataVis/DataTable/ExploreDataSourceTable';
import ExploreDataSourceTableInfiniteScroll from 'components/DataVis/DataTable/ExploreDataSourceTableInfiniteScroll';
import { IndustriesPlaceholderMessage } from 'components/Placeholders';
import NoTikTokDataSection from 'components/NoTikTokDataSection';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import Stack from 'components/Stack';
import TikTokPresetFilterChips from 'components/FilterChips/TikTokPresetFilterChips';
import { healthStatFilterOptions } from 'utils/filters';
import { mixpanelTrack } from 'utils/app';
import useCountryParam from 'utils/hooks/useCountryParam';
import useDataSourceToggle from 'utils/hooks/useDataSourceToggle';

const metrics = [
    [FLAVORS_METADATA_METRIC, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_avg_weekly_views, true],
    [TIKTOK_METADATA_BY_KEY.views, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_WoW, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_WoW, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_MoM_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_MoM_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_QoQ_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_QoQ_rolling, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_YoY_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling, false],
    [TIKTOK_METADATA_BY_KEY.total_posts_cnt, true],
    [TIKTOK_METADATA_BY_KEY.partner_paid_partnership_posts_views_prc, true],
    [TIKTOK_METADATA_BY_KEY.eligible_for_commission_posts_views_prc, false],
    [TIKTOK_METADATA_BY_KEY.engagement_prc, false],
    [TIKTOK_METADATA_BY_KEY.sentiment_score, false],
];

// Brands get a price column; precomputed because the table memoises on the
// array identity.
const brandMetrics = [
    [GOOGLE_METADATA_BY_KEY.price_segmentation, true],
    ...metrics,
];

const buildSortOptions = (forMetrics) =>
    Object.values(TIKTOK_SORT_OPTIONS)
        .concat(FLAVORS_SORT_OPTIONS)
        .concat([
            GOOGLE_SORT_OPTIONS.price_segmentation,
            GOOGLE_SORT_OPTIONS.price_segmentation_asc,
        ])
        .filter(({ sortBy }) => forMetrics.some(([{ key }]) => key === sortBy));

const sortOptions = buildSortOptions(metrics);
const brandSortOptions = buildSortOptions(brandMetrics);
const topicHealthStatKeys = [
    'average weekly views',
    'paid views',
    'sentiment score',
    'posts',
    'engagement',
    'TikTok shop',
];
const hashtagHealthStatKeys = [
    'average weekly views',
    'paid views',
    'posts',
    'engagement',
    'TikTok shop',
];
const tikTokHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => topicHealthStatKeys.includes(label),
);
// The categories tab shares this component, and a category has no price.
const tikTokBrandHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => [...topicHealthStatKeys, 'price point'].includes(label),
);
const tikTokHashtagHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => hashtagHealthStatKeys.includes(label),
);
const filterGroupHashtagColumns = (columns) =>
    columns?.filter(
        (key) => ![TIKTOK_METADATA_BY_KEY.sentiment_score.key].includes(key),
    );

function CategoryTrends({ categoryQuery, categoryId, CategoryComponent }) {
    const { breakdown, type } = useParams();
    const { mutateToWithCountryParam } = useCountryParam();
    const country = useCurrentCountry();
    const trendDimension = type === 'trends' ? 'categories' : type;

    const category = categoryQuery.data?.[0];
    const name = category?.name;

    useEffect(() => {
        if (name && country) {
            const eventName =
                trendDimension === 'brands'
                    ? 'Brand Trends'
                    : 'Category Trends';

            mixpanelTrack(eventName, { name, countryName: country.name });
        }
    }, [name, country, trendDimension]);

    const hashtags = breakdown === 'hashtags' ? '*' : null;
    const brands = trendDimension === 'brands' ? '*' : null;
    const isSubcategoryTrends = trendDimension === 'categories' && !hashtags;
    const categoryDescendants =
        isSubcategoryTrends && categoryId ? [categoryId] : null;
    const categories = !isSubcategoryTrends && categoryId ? [categoryId] : null;
    const paginateBy = !!hashtags ? 'hashtags' : trendDimension;

    return (
        <CategoryComponent
            category={category}
            categoryId={categoryId}
            categoryQuery={categoryQuery}
            country={country}
            hashtags={hashtags}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            name={name}
            paginateBy={paginateBy}
        />
    );
}

function TikTokCategoryTrends({
    category,
    categoryQuery,
    categoryId,
    country,
    hashtags,
    brands,
    categories,
    categoryDescendants,
    trendDimension,
    paginateBy,
    name,
    mixpanelMeta = { contentKey: 'TikTok' },
    filterSelectMenus = ['tikTokCluster', 'healthStats'],
    defaultInitialSortOption = sortOptions.find(
        ({ sortBy }) => sortBy === metrics[1][0].key,
    ),
}) {
    const controller = useAbortController(categoryId);
    const { week, year, query: weeksQuery } = useTikTokWeeks();
    const dataQuery = useTikTokData({
        order_by: defaultInitialSortOption.sortBy,
        ascending: defaultInitialSortOption.ascending,
        paginateBy,
        enabled: !!(
            week &&
            year &&
            country &&
            (categories || categoryDescendants || brands)
        ),
        hashtags,
        brands,
        categories,
        categoryDescendants,
        countries: country ? [country.id] : null,
        week,
        year,
        controller,
    });
    const isLoading = weeksQuery.isLoading || dataQuery.isLoading;

    return (
        <ExploreDataSourceTable
            week={week}
            year={year}
            isLoading={isLoading}
            dataQuery={dataQuery}
            category={category}
            categoryQuery={categoryQuery}
            categoryId={categoryId}
            country={country}
            hashtags={hashtags}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            paginateBy={paginateBy}
            name={name}
            tableName="explore-tiktok"
            NameBodyColComponent={TikTokNameHashtagListCol}
            hashtagHealthStatFilterOptions={
                tikTokHashtagHealthStatFilterOptions
            }
            healthStatFilterOptions={
                brands
                    ? tikTokBrandHealthStatFilterOptions
                    : tikTokHealthStatFilterOptions
            }
            getDownloadOptions={getTikTokDownloadOptions}
            source="tiktok"
            mixpanelMeta={mixpanelMeta}
            filterSelectMenus={filterSelectMenus}
            filterColumns={!!hashtags ? filterGroupHashtagColumns : null}
            sortOptions={brands ? brandSortOptions : sortOptions}
            metrics={brands ? brandMetrics : metrics}
            defaultInitialSortOption={defaultInitialSortOption}
            PresetFilterChipsComponent={TikTokPresetFilterChips}
            DrawerComponent={Drawer}
            DrawerContentDefaultComponent={TikTokDrawerContent}
            DrawerContentHashtagComponent={TikTokDrawerContentIndividualHashtag}
        />
    );
}

function TikTokHashtagTrends({
    category,
    categoryQuery,
    categoryId,
    country,
    hashtags,
    brands,
    categories,
    categoryDescendants,
    trendDimension,
    paginateBy,
    name,
    mixpanelMeta = { contentKey: 'TikTok' },
    filterSelectMenus = ['tikTokCluster', 'healthStats'],
    defaultInitialSortOption = sortOptions.find(
        ({ sortBy }) => sortBy === metrics[1][0].key,
    ),
}) {
    return (
        <ExploreDataSourceTableInfiniteScroll
            category={category}
            categoryQuery={categoryQuery}
            categoryId={categoryId}
            country={country}
            hashtags={hashtags}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            paginateBy={paginateBy}
            name={name}
            tableName="explore-tiktok"
            NameBodyColComponent={TikTokNameBodyCol}
            hashtagHealthStatFilterOptions={
                tikTokHashtagHealthStatFilterOptions
            }
            healthStatFilterOptions={tikTokHealthStatFilterOptions}
            getDownloadOptions={getTikTokDownloadOptionsAsync}
            source="tiktok"
            mixpanelMeta={mixpanelMeta}
            filterSelectMenus={filterSelectMenus}
            filterColumns={!!hashtags ? filterGroupHashtagColumns : null}
            sortOptions={sortOptions}
            metrics={metrics}
            defaultInitialSortOption={defaultInitialSortOption}
            PresetFilterChipsComponent={TikTokPresetFilterChips}
            DrawerComponent={Drawer}
            DrawerContentDefaultComponent={TikTokDrawerContent}
            DrawerContentHashtagComponent={TikTokDrawerContentIndividualHashtag}
        />
    );
}

function TikTokBrands({
    breakdown,
    categoryId,
    cat1,
    geo,
    children,
    generateLocation,
    isNotTikTokMarket,
    location,
    mutateToWithCountryParam,
}) {
    const { modes } = useDataSourceToggle();
    return (
        <Stack $space="1.5rem">
            {breakdown === 'hashtags' || !categoryId ? (
                <div
                    css={`
                        position: relative;
                        z-index: 1;
                    `}
                >
                    <DataSourceIndustriesOnlyBreadCrumbs
                        dataSource="TIKTOK"
                        cat1={cat1}
                        geo={geo}
                        generateLocation={generateLocation}
                        {...(categoryId && {
                            id: categoryId,
                            isActive: true,
                        })}
                    />
                </div>
            ) : (
                <div
                    css={`
                        position: relative;
                        z-index: 1;
                        max-width: calc((100% - 120px) / 2);
                    `}
                >
                    <DataSourceBreadCrumbs
                        dataSource="TIKTOK"
                        geo={geo}
                        cat1={cat1}
                        showIndustriesMenu={true}
                        generateLocation={generateLocation}
                        maxDepth={2}
                        replace={true}
                        skeletonSize={2}
                    />
                </div>
            )}
            {breakdown === 'hashtags' ? (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    unsupportedTabs={[modes.OVERVIEW, modes.SEARCH]}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/hashtags/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            ) : (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    customOverviewTo={mutateToWithCountryParam(
                        `/explore/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customGoogleTo={mutateToWithCountryParam(
                        `/explore/google/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            )}
            {children}
        </Stack>
    );
}

function TikTokCatFives({
    breakdown,
    categoryId,
    children,
    generateLocation,
    isNotTikTokMarket,
    location,
    mutateToWithCountryParam,
    geo,
    cat1,
}) {
    const { modes } = useDataSourceToggle();
    return (
        <Stack $space="1.5rem">
            <div
                css={`
                    position: relative;
                    z-index: 1;
                `}
            >
                <DataSourceBreadCrumbs
                    dataSource="TIKTOK"
                    geo={geo}
                    cat1={cat1}
                    showIndustriesMenu={true}
                    generateLocation={generateLocation}
                    maxDepth={4}
                    replace={true}
                    defaultOpen={breakdown !== 'hashtags'}
                />
            </div>
            {breakdown === 'hashtags' ? (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    unsupportedTabs={[modes.OVERVIEW, modes.SEARCH]}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/hashtags/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            ) : (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    customOverviewTo={mutateToWithCountryParam(
                        `/explore/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customGoogleTo={mutateToWithCountryParam(
                        `/explore/google/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            )}
            {children}
        </Stack>
    );
}

function TikTokTrends() {
    const country = useCurrentCountry();
    const { mutateToWithCountryParam } = useCountryParam();
    const location = useLocation();
    const { path } = useRouteMatch();
    const { breakdown, type, categoryId } = useParams();
    const categoryQuery = useEntities({
        enabled: !!categoryId,
        ids: [categoryId],
        entityType: 'categories',
        dataSource: DATA_SOURCES.TIKTOK,
    });
    const generateLocation = ({ id }) => ({
        pathname: generatePath(path, { breakdown, type, categoryId: id }),
        search: location.search,
    });
    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);
    const DimensionComponent =
        type === 'trends' ? TikTokCatFives : TikTokBrands;
    const CategoryComponent =
        breakdown === 'hashtags' ? TikTokHashtagTrends : TikTokCategoryTrends;
    const industryName = categoryQuery?.data?.[0]?.hierarchies?.[0]?.[1]?.cat1;
    return (
        <PagePadding className="max-w-screen-2xl">
            <PageBar />
            <Stack $space="1.25rem">
                <DimensionComponent
                    breakdown={breakdown}
                    categoryId={categoryId}
                    generateLocation={generateLocation}
                    isNotTikTokMarket={isNotTikTokMarket}
                    location={location}
                    mutateToWithCountryParam={mutateToWithCountryParam}
                    geo={country?.text_id}
                    cat1={categoryQuery?.data?.[0]?.hierarchies?.[0]?.[1]?.cat1}
                >
                    {(industryName &&
                        country &&
                        !isCovered({
                            geo: country?.text_id,
                            dataSource: 'TIKTOK',
                            cat1: industryName,
                        })) ||
                    (!categoryId &&
                        country?.text_id &&
                        !TIKTOK_COUNTRIES.includes(country?.text_id)) ? (
                        <NoTikTokDataSection />
                    ) : !categoryId && breakdown !== 'hashtags' ? (
                        <IndustriesPlaceholderMessage />
                    ) : (
                        <CategoryTrends
                            categoryQuery={categoryQuery}
                            categoryId={categoryId}
                            CategoryComponent={CategoryComponent}
                        />
                    )}
                </DimensionComponent>
            </Stack>
        </PagePadding>
    );
}

export default TikTokTrends;
